import { Head, Link, useForm, usePage } from "@inertiajs/react";
import { ArrowLeft, Save } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AppLayout from "@/layouts/app-layout";
import { organizationCategoriesPath, organizationCategoryPath } from "@/routes";
import type { BreadcrumbItem, Category, SharedData } from "@/types";

interface CategoryEditProps {
  category: Category;
}

export default function CategoryEdit({ category }: CategoryEditProps) {
  const { auth } = usePage<SharedData>().props;
  const organizationSlug = auth.current_organization?.slug ?? "";
  const categoriesHref = organizationCategoriesPath(organizationSlug);
  const categoryHref = organizationCategoryPath(organizationSlug, category);
  const { data, setData, patch, processing, errors } = useForm({
    name: category.name ?? "",
    description: category.description ?? "",
  });

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: "Categorías",
      href: categoriesHref,
    },
    {
      title: category.name,
      href: categoryHref,
    },
    {
      title: "Editar",
      href: "#",
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    patch(categoryHref);
  };

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Editar ${category.name}`} />
      <div className="flex h-full flex-1 flex-col gap-6 p-4">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={categoryHref}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Editar Categoría</h1>
            <p className="text-muted-foreground">
              Modifica la información de la categoría &quot;{category.name}&quot;
            </p>
          </div>
        </div>

        {/* Form */}
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle>Información de la Categoría</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  placeholder="Ingrese el nombre de la categoría"
                  disabled={processing}
                />
                {errors.name && (
                  <p className="text-sm text-red-500">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descripción (opcional)</Label>
                <Textarea
                  id="description"
                  value={data.description}
                  onChange={(e) => setData("description", e.target.value)}
                  placeholder="Descripción de la categoría"
                  disabled={processing}
                  rows={4}
                />
                {errors.description && (
                  <p className="text-sm text-red-500">{errors.description}</p>
                )}
              </div>

              <div className="flex items-center gap-4">
                <Button type="submit" disabled={processing}>
                  <Save className="h-4 w-4 mr-2" />
                  {processing ? "Actualizando..." : "Actualizar Categoría"}
                </Button>
                <Button type="button" variant="outline" asChild>
                  <Link href={categoryHref}>Cancelar</Link>
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
